import React, { useState, useEffect } from 'react';
import { Flex, Spinner, Panel } from '@maxhub/max-ui';
import { api } from '../api/client';
import { Booking, Guest, HistoryBooking } from '../types';
import { notify } from '../utils/notify';

type TabType = 'active' | 'history' | 'guests';

export const MyBookingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('active');
  const [activeBookings, setActiveBookings] = useState<Booking[]>([]);
  const [history, setHistory] = useState<HistoryBooking[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<{ id: number; note: string } | null>(null);
  const [ownerLink, setOwnerLink] = useState<string | null>(null);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [activeRes, historyRes, guestsRes, meRes] = await Promise.all([
        api.get('/my-bookings'),
        api.get('/my-history'),
        api.get('/my-guests'),
        api.get('/me'),
      ]);
      setActiveBookings(activeRes.data);
      setHistory(historyRes.data);
      setGuests(guestsRes.data);
      setOwnerLink(meRes.data.owner_link || null);
    } catch (err) {
      setError('Не удалось загрузить данные');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleSaveNote = async (bookingId: number, note: string) => {
    try {
      await api.put(`/bookings/${bookingId}/note`, { note });
      notify.success('Заметка сохранена');
      setHistory(prev =>
        prev.map(b =>
          b.id === bookingId ? { ...b, note } : b
        )
      );
      setEditingNote(null);
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Ошибка сохранения заметки';
      notify.error(msg);
    }
  };

  const handleCancelBooking = async (bookingId: number) => {
    try {
      await api.delete(`/bookings/${bookingId}`);
      notify.success('Запись отменена');
      await loadData();
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Не удалось отменить запись';
      notify.error(msg);
    }
  };

  if (loading) {
    return (
      <Flex style={{ justifyContent: 'center', alignItems: 'center', padding: 60 }}>
        <Spinner size={40} />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex style={{ justifyContent: 'center', alignItems: 'center', padding: 40, flexDirection: 'column' }}>
        <p style={{ fontSize: 14 }}>{error}</p>
        <button onClick={loadData} style={{ padding: '8px 16px', cursor: 'pointer', marginTop: 8 }}>
          Попробовать снова
        </button>
      </Flex>
    );
  }

  return (
    <Panel style={{ padding: 16 }}>
      {/* ✅ Шапка с заголовком и кнопкой администратора */}
      <Flex style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 8 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>👤 Личный кабинет</h1>
        {ownerLink && (
          <a
            href={ownerLink}
            target="_blank"
            rel="noopener noreferrer"
            title="Написать администратору"
            style={{
              padding: '8px 12px',
              backgroundColor: '#007AFF',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: 8,
              fontSize: 13,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              whiteSpace: 'nowrap',
              fontWeight: 'bold',
            }}
          >
            📝 Администратор
          </a>
        )}
      </Flex>

      {/* Вкладки */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e0e0e0',
        marginBottom: 16,
        gap: 0,
      }}>
        <button
          onClick={() => setActiveTab('active')}
          style={{
            padding: '10px 16px',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            fontSize: 14,
            fontWeight: activeTab === 'active' ? 'bold' : 'normal',
            color: activeTab === 'active' ? '#007AFF' : '#666',
            borderBottom: activeTab === 'active' ? '2px solid #007AFF' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          📌 Активные записи
          {activeBookings.length > 0 && (
            <span style={{
              marginLeft: 6,
              background: '#007AFF',
              color: '#fff',
              borderRadius: 12,
              padding: '0 8px',
              fontSize: 12,
            }}>
              {activeBookings.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '10px 16px',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            fontSize: 14,
            fontWeight: activeTab === 'history' ? 'bold' : 'normal',
            color: activeTab === 'history' ? '#007AFF' : '#666',
            borderBottom: activeTab === 'history' ? '2px solid #007AFF' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          📜 История
          {history.length > 0 && (
            <span style={{
              marginLeft: 6,
              background: '#9e9e9e',
              color: '#fff',
              borderRadius: 12,
              padding: '0 8px',
              fontSize: 12,
            }}>
              {history.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('guests')}
          style={{
            padding: '10px 16px',
            cursor: 'pointer',
            border: 'none',
            background: 'none',
            fontSize: 14,
            fontWeight: activeTab === 'guests' ? 'bold' : 'normal',
            color: activeTab === 'guests' ? '#007AFF' : '#666',
            borderBottom: activeTab === 'guests' ? '2px solid #007AFF' : '2px solid transparent',
            transition: 'all 0.2s',
          }}
        >
          👥 Мои гости
          {guests.length > 0 && (
            <span style={{
              marginLeft: 6,
              background: '#4CAF50',
              color: '#fff',
              borderRadius: 12,
              padding: '0 8px',
              fontSize: 12,
            }}>
              {guests.length}
            </span>
          )}
        </button>
      </div>

      <div style={{ marginTop: 8 }}>
        {activeTab === 'active' && (
          <>
            {activeBookings.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '20px 0' }}>
                У вас нет активных записей
              </p>
            ) : (
              activeBookings.map(booking => {
                const event = booking.event;
                if (!event) return null;
                const date = new Date(event.datetime);
                const formattedDate = date.toLocaleDateString('ru-RU', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                });
                const formattedTime = date.toLocaleTimeString('ru-RU', {
                  hour: '2-digit', minute: '2-digit',
                });

                const hoursUntilEvent = (date.getTime() - Date.now()) / (1000 * 60 * 60);
                const canCancel = hoursUntilEvent >= 72;

                return (
                  <div key={booking.id} style={{ marginBottom: 16, padding: 16, backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 'bold', margin: '0 0 4px 0' }}>
                      {booking.is_guest ? `👤 Гость: ${booking.guest_name || 'Без имени'}` : event.title}
                    </h3>
                    <p style={{ fontSize: 14, color: '#666', margin: '4px 0 8px 0' }}>
                      📅 {formattedDate} в {formattedTime}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 16, border: '1px solid #ddd', fontSize: 14 }}>
                        💰 {event.price} ₽
                      </span>
                      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 16, backgroundColor: '#4CAF50', color: '#fff', fontSize: 14 }}>
                        {booking.is_guest ? 'Запись оформлена' : 'Записано'}
                      </span>
                      {booking.status === 'pending_payment' && !booking.payment_confirmed && (
                        <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 16, backgroundColor: '#ff9800', color: '#fff', fontSize: 14 }}>
                          ⏳ Ожидает оплаты
                        </span>
                      )}
                    </div>

                    {/* Кнопка отмены */}
                    <div style={{ marginTop: 12 }}>
                      {canCancel ? (
                        <button
                          onClick={() => {
                            if (window.confirm(
                              `Вы уверены, что хотите отменить запись${booking.is_guest ? ` для гостя «${booking.guest_name}»` : ''} на мероприятие "${event.title}"?`
                            )) {
                              handleCancelBooking(booking.id);
                            }
                          }}
                          style={{
                            padding: '6px 16px',
                            cursor: 'pointer',
                            background: '#fff',
                            color: '#ff4444',
                            border: '1px solid #ff4444',
                            borderRadius: 6,
                            fontSize: 13,
                          }}
                        >
                          ❌ Отменить запись
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
                          ❌ Отмена невозможна (до мероприятия меньше 3 суток). Свяжитесь с администратором.
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {activeTab === 'history' && (
          <>
            {history.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '20px 0' }}>
                У вас пока нет завершённых мероприятий
              </p>
            ) : (
              history.map(item => {
                const event = item.event;
                if (!event) return null;
                const date = new Date(event.datetime);
                const formattedDate = date.toLocaleDateString('ru-RU', {
                  day: '2-digit', month: '2-digit', year: 'numeric',
                });
                const formattedTime = date.toLocaleTimeString('ru-RU', {
                  hour: '2-digit', minute: '2-digit',
                });

                const isEditing = editingNote?.id === item.id;

                return (
                  <div key={item.id} style={{ marginBottom: 16, padding: 16, backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <h3 style={{ fontSize: 16, fontWeight: 'bold', margin: '0 0 4px 0' }}>
                      {item.is_guest ? `👤 Гость: ${item.guest_name || 'Без имени'}` : event.title}
                    </h3>
                    <p style={{ fontSize: 14, color: '#666', margin: '4px 0 8px 0' }}>
                      📅 {formattedDate} в {formattedTime}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 16, border: '1px solid #ddd', fontSize: 14 }}>
                        💰 {event.price} ₽
                      </span>
                      <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 16, backgroundColor: '#9e9e9e', color: '#fff', fontSize: 14 }}>
                        ✅ Завершено
                      </span>
                    </div>

                    <div style={{ marginTop: 8 }}>
                      {isEditing ? (
                        <div>
                          <textarea
                            defaultValue={item.note || ''}
                            rows={3}
                            style={{ width: '100%', padding: 6, borderRadius: 4, border: '1px solid #ddd' }}
                            id={`note-${item.id}`}
                          />
                          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                            <button
                              onClick={() => {
                                const textarea = document.getElementById(`note-${item.id}`) as HTMLTextAreaElement;
                                if (textarea) handleSaveNote(item.id, textarea.value);
                              }}
                              style={{ padding: '4px 12px', cursor: 'pointer', background: '#007AFF', color: '#fff', border: 'none', borderRadius: 4 }}
                            >
                              Сохранить
                            </button>
                            <button
                              onClick={() => setEditingNote(null)}
                              style={{ padding: '4px 12px', cursor: 'pointer', background: '#f0f0f0', border: 'none', borderRadius: 4 }}
                            >
                              Отмена
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <div style={{ flex: 1, fontSize: 14, color: '#333', background: '#f5f5f5', padding: '6px 10px', borderRadius: 4, whiteSpace: 'pre-wrap' }}>
                            {item.note ? item.note : <span style={{ color: '#999' }}>Нет заметки</span>}
                          </div>
                          <button
                            onClick={() => setEditingNote({ id: item.id, note: item.note || '' })}
                            style={{ padding: '4px 12px', cursor: 'pointer', background: 'none', border: '1px solid #ddd', borderRadius: 4, fontSize: 13 }}
                          >
                            ✏️ Редактировать
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {activeTab === 'guests' && (
          <>
            {guests.length === 0 ? (
              <p style={{ textAlign: 'center', color: '#666', padding: '20px 0' }}>
                Вы ещё не записывали гостей
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
                {guests.map(guest => (
                  <div key={guest.id} style={{ padding: 12, backgroundColor: '#fff', borderRadius: 8, boxShadow: '0 2px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontWeight: 'bold', fontSize: 16 }}>{guest.full_name}</div>
                    <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                      {guest.created_at ? `Добавлен: ${new Date(guest.created_at).toLocaleDateString('ru-RU')}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Panel>
  );
};